import {
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  type AssignedTo,
  fetchCompletedTasks,
  fetchOpenTasks,
  fetchSubjectOpenTasks,
  fetchTaskCounts,
} from "@tasks/tasksClient";

export const tasksKeys = {
  /** Prefix for invalidating every tasks query. */
  all: ["tasks"] as const,
  subjectOpen: (sessionKey: string, subject: string) =>
    ["tasks", sessionKey, "subject-open", subject] as const,
  subjectCompleted: (sessionKey: string, subject: string) =>
    ["tasks", sessionKey, "subject-completed", subject] as const,
  // `assignedTo` is part of the key so a leadership "see all" view caches
  // separately from the default "my tasks" view (BE-2089).
  counts: (sessionKey: string, assignedTo: AssignedTo = "me") =>
    ["tasks", sessionKey, "counts", assignedTo] as const,
  open: (
    sessionKey: string,
    kind?: string,
    urgent?: boolean,
    assignedTo: AssignedTo = "me",
  ) =>
    [
      "tasks",
      sessionKey,
      "open",
      kind ?? null,
      urgent ?? false,
      assignedTo,
    ] as const,
};

/** A subject's open tasks — the rail's "Needs you" set (WEB-3300). */
export const subjectOpenTasksQueryOptions = (
  sessionKey: string,
  subject: string,
) => ({
  queryKey: tasksKeys.subjectOpen(sessionKey, subject),
  queryFn: () => fetchSubjectOpenTasks(subject),
});

/**
 * A subject's completed to-dos, paged (WEB-3300). Plain, not suspense: a fourth
 * suspense query would resolve serially after `useSubjectIndex`'s batch and delay
 * the whole rail for supplementary history. The group hides until it lands, and
 * says so rather than vanishing once the retries fail.
 */
export const useCompletedTasks = (sessionKey: string, subject: string) =>
  useInfiniteQuery({
    queryKey: tasksKeys.subjectCompleted(sessionKey, subject),
    queryFn: ({ pageParam }) => fetchCompletedTasks(subject, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

export const useTaskCounts = (
  sessionKey: string,
  assignedTo: AssignedTo = "me",
) =>
  useSuspenseQuery({
    queryKey: tasksKeys.counts(sessionKey, assignedTo),
    queryFn: () => fetchTaskCounts(assignedTo),
  });

/**
 * The open tasks the caller may see, for the clinic-wide task list — ordered
 * urgent-first then by due date server-side (BE-2133). `kind` and `urgent`
 * filter server-side, so each filter combination pages
 * independently. `assignedTo` scopes visibility (BE-2089): `me` (default) is the
 * caller's own user/role tasks; `all` is the whole clinic, honored only for
 * leadership. Paging follows the server's opaque `nextCursor`: it decides where
 * the next page starts and when the list is exhausted, so the client neither
 * builds the token nor infers "last page" from a short one.
 */
export const useOpenTasks = (
  sessionKey: string,
  kind?: string,
  urgent?: boolean,
  assignedTo: AssignedTo = "me",
) =>
  useSuspenseInfiniteQuery({
    queryKey: tasksKeys.open(sessionKey, kind, urgent, assignedTo),
    queryFn: ({ pageParam }) =>
      fetchOpenTasks(pageParam, kind, urgent, assignedTo),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
